import React, { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, Calendar, FileText, Scale } from "lucide-react";

export default function Terms() {
  const [activeSection, setActiveSection] = useState("overview");

  const sections = [
    { id: "overview", title: "Overview" },
    { id: "acceptance", title: "Acceptance of Terms" },
    { id: "service-description", title: "Description of Service" },
    { id: "user-accounts", title: "User Accounts" },
    { id: "acceptable-use", title: "Acceptable Use" },
    { id: "subscription-billing", title: "Subscription & Billing" },
    { id: "intellectual-property", title: "Intellectual Property" },
    { id: "privacy-data", title: "Privacy and Data" },
    { id: "service-availability", title: "Service Availability" },
    { id: "limitation-liability", title: "Limitation of Liability" },
    { id: "indemnification", title: "Indemnification" },
    { id: "third-party", title: "Third-Party Integrations" },
    { id: "termination", title: "Termination" },
    { id: "modifications", title: "Modifications to Terms" },
    { id: "governing-law", title: "Governing Law" },
    { id: "contact", title: "Contact Information" }
  ];

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header currentPage="legal" variant="gallery" />
      
      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="w-80 bg-slate-50 dark:bg-slate-800 min-h-screen pt-8 pb-16 sticky top-0 border-r border-slate-200 dark:border-slate-700">
          <div className="px-6">
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Terms of Service</h2>
              <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 mb-2">
                <Calendar className="h-4 w-4 mr-2" />
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
                <FileText className="h-4 w-4 mr-2" />
                Version 1.0
              </div>
            </div>
            
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                    activeSection === section.id
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 font-medium'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </nav>
            
            <div className="mt-8 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-300 mb-2">Legal Questions?</h3>
              <p className="text-xs text-purple-700 dark:text-purple-400 mb-3">
                Questions about these terms?
              </p>
              <a
                href="mailto:legal@lavenderflow.dev"
                className="inline-flex items-center text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
              >
                <Scale className="h-3 w-3 mr-1" />
                legal@lavenderflow.dev
              </a>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 py-12 px-8 max-w-4xl">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <section id="overview">
              <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-6">Terms of Service</h1>
              <p className="text-xl text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
                Welcome to Lavender Flow. These Terms of Service govern your access to and use of our visual, 
                node-based platform for building and deploying multimodal AI agent pipelines.
              </p>
            </section>

            <section id="acceptance" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">1. Acceptance of Terms</h2>
              <p className="mb-6">
                By accessing or using Lavender Flow ("the Service"), you agree to be bound by these Terms of Service 
                and our Privacy Policy. If you do not agree to these terms, please do not use our service.
              </p>
            </section>

            <section id="service-description" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">2. Description of Service</h2>
              <p className="mb-6">
                Lavender Flow is a visual, node-based platform for building and deploying multimodal AI agent pipelines. 
                The Service supports text, images, video, audio, and real-time streaming data processing through 
                customizable workflows.
              </p>
            </section>

            <section id="user-accounts" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">3. User Accounts</h2>
              <p className="mb-4">
                To use certain features of the Service, you must create an account. You are responsible for:
              </p>
              <ul className="mb-6">
                <li>Maintaining the security of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Providing accurate and current information</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section id="acceptable-use" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">4. Acceptable Use</h2>
              <p className="mb-4">You agree not to use the Service to:</p>
              <ul className="mb-6">
                <li>Violate any laws or regulations</li>
                <li>Infringe upon intellectual property rights</li>
                <li>Distribute harmful, offensive, or illegal content</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use the service for competitive analysis or reverse engineering</li>
                <li>Create workflows that violate third-party AI service terms</li>
              </ul>
            </section>

            <section id="subscription-billing" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">5. Subscription Plans and Billing</h2>
              
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-6 mb-6 border border-slate-200 dark:border-slate-700">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">5.1 Plan Types</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="p-3 bg-white dark:bg-slate-700 rounded border">
                      <div className="font-semibold text-slate-900 dark:text-white">Free Plan</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Limited usage with $5 signup credits</div>
                    </div>
                    <div className="p-3 bg-white dark:bg-slate-700 rounded border">
                      <div className="font-semibold text-slate-900 dark:text-white">Pro Plan</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">$29/month (annual) or $34.80/month (monthly) with $10 credits</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="p-3 bg-white dark:bg-slate-700 rounded border">
                      <div className="font-semibold text-slate-900 dark:text-white">Teams Plan</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">$99/month (annual) for 3 users or $118.80/month (monthly) with $20 credits</div>
                    </div>
                    <div className="p-3 bg-white dark:bg-slate-700 rounded border">
                      <div className="font-semibold text-slate-900 dark:text-white">Teams+ Plan</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">$50/month per user (annual) or $60/month per user (monthly) with $20 credits</div>
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">5.2 Usage-Based Costs</h3>
              <p className="mb-4">
                Additional charges may apply for CPU/GPU runtime and external API usage. You may use your own 
                API keys or opt for managed billing through our platform.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">5.3 Payment Terms</h3>
              <p className="mb-6">
                Subscription fees are billed in advance. Usage-based costs are billed monthly. 
                All payments are non-refundable except as required by law.
              </p>
            </section>

            <section id="intellectual-property" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">6. Intellectual Property</h2>
              
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">6.1 Our Rights</h3>
              <p className="mb-4">
                Lavender Flow and all related trademarks, logos, and intellectual property remain our exclusive property.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">6.2 Your Content</h3>
              <p className="mb-4">
                You retain ownership of your workflows, data, and content. By using our Service, you grant us 
                a limited license to host, store, and process your content to provide the Service.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">6.3 Shared Flows</h3>
              <p className="mb-6">
                When you publish Flows to our gallery, you grant other users the right to use those Flows 
                in their workflows according to the terms you specify.
              </p>
            </section>

            <section id="privacy-data" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">7. Privacy and Data</h2>
              <p className="mb-6">
                Your privacy is important to us. Please review our <a href="/legal/privacy" className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300">Privacy Policy</a> to understand how we 
                collect, use, and protect your information.
              </p>
            </section>

            <section id="service-availability" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">8. Service Availability</h2>
              <p className="mb-6">
                We strive to maintain high availability but do not guarantee uninterrupted service. 
                We may perform maintenance, updates, or experience technical issues that temporarily 
                affect service availability.
              </p>
            </section>

            <section id="limitation-liability" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">9. Limitation of Liability</h2>
              <p className="mb-6">
                To the maximum extent permitted by law, Lavender Flow shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages, including but not limited to loss 
                of profits, data, or use.
              </p>
            </section>

            <section id="indemnification" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">10. Indemnification</h2>
              <p className="mb-6">
                You agree to indemnify and hold harmless Lavender Flow from any claims, damages, or expenses 
                arising from your use of the Service or violation of these terms.
              </p>
            </section>

            <section id="third-party" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">11. Third-Party Integrations</h2>
              <p className="mb-6">
                Our Service integrates with third-party AI providers and services. Your use of these 
                integrations is subject to their respective terms of service and privacy policies.
              </p>
            </section>

            <section id="termination" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">12. Termination</h2>
              <p className="mb-4">
                Either party may terminate this agreement at any time. Upon termination:
              </p>
              <ul className="mb-6">
                <li>Your access to the Service will be suspended</li>
                <li>You may export your data within 30 days</li>
                <li>We may delete your data after the export period</li>
                <li>Outstanding charges remain due</li>
              </ul>
            </section>

            <section id="modifications" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">13. Modifications to Terms</h2>
              <p className="mb-6">
                We reserve the right to modify these terms at any time. Material changes will be 
                communicated via email or platform notification. Continued use constitutes acceptance 
                of modified terms.
              </p>
            </section>

            <section id="governing-law" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">14. Governing Law</h2>
              <p className="mb-6">
                These terms are governed by the laws of [Jurisdiction] without regard to conflict 
                of law principles. Any disputes shall be resolved through binding arbitration.
              </p>
            </section>

            <section id="contact" className="mt-12 mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">15. Contact Information</h2>
              <p className="mb-4">
                For questions about these terms, please contact us:
              </p>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Legal Department</h4>
                    <a href="mailto:legal@lavenderflow.dev" className="inline-flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300">
                      <Scale className="h-4 w-4 mr-2" />
                      legal@lavenderflow.dev
                    </a>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Support Center</h4>
                    <a href="https://lavenderflow.dev/help" className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300">
                      https://lavenderflow.dev/help
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <h4 className="font-semibold text-yellow-900 dark:text-yellow-300 mb-2">Severability</h4>
                <p className="text-sm text-yellow-800 dark:text-yellow-400">
                  If any provision of these terms is found to be unenforceable, the remaining provisions 
                  will remain in full force and effect.
                </p>
              </div>
            </section>
          </div>
        </main>
      </div>
      
      <Footer />
    </div>
  );
}